import { FC, useEffect, useState } from 'react';
import { IFlyingHours } from '../../../model/IFlyingHours';
import { GoAButton, GoAButtonGroup, GoAModal } from '@abgov/react-components';
import FlyingHoursEditTable from './FlyingHoursEditTable';
import { IPaginationResult } from '../../../model/search/IPaginationResult';
import { IContractRates } from '../../../model/HiringContract/IContractRates';
import ViewFlyingDetail from './ViewFlyingDetail';
import { IFlightReport } from '../../../model/IFlightReport';
import { EmptyGuid } from '../../../utils/Constants';
import { IDomainData } from '../helpers/DomainDataHelper';
import { convertAccountsToArrayOptions } from '../../../services/1GXCoding/AccountService';
import { convertCostCentersToArrayOptions } from '../../../services/1GXCoding/CostCenterService';
import { convertFundsToArrayOptions } from '../../../services/1GXCoding/FundService';
import { convertInternalOrdersToArrayOptions } from '../../../services/1GXCoding/InternalOrderService';
import { convertCostingActivitiesToArrayOptions } from '../../../services/domainServices/CostingActivityService';
import { convertCrewTypesToArrayOptions } from '../../../services/domainServices/CrewTypeService';
import { convertFireNumbersToArrayOptions } from '../../../services/domainServices/FireNumberService';
import { convertRateTypesToArrayOptions } from '../../../services/domainServices/RateTypeService';
import { convertRateUnitsToArrayOptions } from '../../../services/domainServices/RateUnitService';
import { IArrayOption } from '../../../model/IArrayOption';

interface IFlyingHoursTabProps {
  flightReport: IFlightReport;
  noFlyDay: boolean;
  canEdit: boolean;
  canSave: boolean;
  isValid: boolean;
  flyingHours: IFlyingHours[];
  contractRates: IPaginationResult<IContractRates>;
  onValidate: (elementName: string, eventName: string, value: any) => void;
  onChange: (hours: IFlyingHours[]) => void;
  onCancel: () => void;
  onSave: () => Promise<boolean>;
  domainData: IDomainData;
}
const FlyingHoursTab: FC<IFlyingHoursTabProps> = ({
  flightReport,
  flyingHours,
  noFlyDay,
  canEdit,
  canSave,
  onValidate,
  contractRates,
  isValid,
  onChange,
  onSave,
  onCancel,
  domainData,
}) => {
  const [isEdit, setIsEdit] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [fireNumberOptions, setFireNumberOptions] = useState<IArrayOption[]>([]);
  const [crewTypeOptions, setCrewTypeOptions] = useState<IArrayOption[]>([]);
  const [costingActivityOptions, setCostingActivityOptions] = useState<IArrayOption[]>([]);

  const [rateTypeOptions, setRateTypeOptions] = useState<IArrayOption[]>([]);
  const [rateUnitOptions, setRateUnitOptions] = useState<IArrayOption[]>([]);
  const [internalOrderOptions, setInternalOrderOptions] = useState<IArrayOption[]>([]);
  const [costCenterOptions, setCostCenterOptions] = useState<IArrayOption[]>([]);
  const [fundOptions, setFundOptions] = useState<IArrayOption[]>([]);
  const [accountOptions, setAccountOptions] = useState<IArrayOption[]>([]);

  useEffect(() => {
    if (!domainData) return;

    const fireNumberOptions = convertFireNumbersToArrayOptions(domainData.fireNumbers);
    setFireNumberOptions(fireNumberOptions);

    const crewTypeOptions = convertCrewTypesToArrayOptions(domainData.crewTypes);
    setCrewTypeOptions(crewTypeOptions);

    const costingActivityOptions = convertCostingActivitiesToArrayOptions(domainData.costingActivities);
    setCostingActivityOptions(costingActivityOptions);

    const rateTypeOptions = convertRateTypesToArrayOptions(domainData.rateTypes);
    setRateTypeOptions(rateTypeOptions);

    const rateUnitOptions = convertRateUnitsToArrayOptions(domainData.rateUnits);
    setRateUnitOptions(rateUnitOptions);

    const internalOrderOptions = convertInternalOrdersToArrayOptions(domainData.internalOrders);
    setInternalOrderOptions(internalOrderOptions);

    const costCenterOptions = convertCostCentersToArrayOptions(domainData.costCenters);
    setCostCenterOptions(costCenterOptions);

    const fundOptions = convertFundsToArrayOptions(domainData.funds);
    setFundOptions(fundOptions);
    const accountOptions = convertAccountsToArrayOptions(domainData.accounts);
    setAccountOptions(accountOptions);
  }, [domainData]);

  function onFlyingHoursDelete() {
    if (flyingHours.length > 0) {
      const updated = flyingHours.map((x) => ({
        ...x,
        deleted: x.isChecked ? true : x.deleted,
      }));
      // if flightReportCostDetailsId is emptyGuid and deleted is true, remove them
      const filtered = updated.filter((x) => !(x.flightReportCostDetailsId === EmptyGuid && x.deleted));
      onChange(filtered);
    }
    setShowDeleteModal(false);
  }

  async function saveFlyingHours() {
    const success = await onSave();
    setIsEdit(!success);
  }

  return (
    <>
      {!isEdit && canEdit && (
        <div className='right paddingRight20px'>
          <GoAButton
            size='compact'
            type='tertiary'
            disabled={noFlyDay}
            onClick={() => {
              setIsEdit(true);
            }}
          >
            Edit
          </GoAButton>
        </div>
      )}
      {!isEdit && <ViewFlyingDetail flyingHours={flyingHours} />}
      {isEdit && canSave && (
        <>
          <div className='right paddingRight20px'>
            <GoAButton
              size='compact'
              type='tertiary'
              disabled={!isValid || !!flyingHours.find((x) => x.isNoOfUnitsError) || !!flyingHours.find((x) => x.isRatePerUnitError)}
              onClick={saveFlyingHours}
            >
              Save
            </GoAButton>
            <GoAButton
              size='compact'
              type='tertiary'
              onClick={() => {
                onCancel();
                setIsEdit(false);
              }}
            >
              Cancel
            </GoAButton>
          </div>
          {flyingHours.some((x) => !x.deleted && x.isChecked) && (
            <div className='flyingHoursDelete'>
              <div className='row accordion'>
                <div className='form-row width100 setBackground'>
                  <div className='col-50 white-color'>{`${flyingHours.filter((x) => !x.deleted && x.isChecked).length} selected`}</div>
                  <div className='col-50'>
                    <div className='right'>
                      {canEdit && (
                        <div>
                          <GoAButton
                            size='compact'
                            type='secondary'
                            trailingIcon='trash'
                            disabled={!isValid}
                            onClick={() => {
                              setShowDeleteModal(true);
                            }}
                          >
                            Delete
                          </GoAButton>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {isEdit && (
            <FlyingHoursEditTable
              flightReport={flightReport}
              noFlyDay={noFlyDay}
              flyingHours={flyingHours}
              onFlyingHoursChange={onChange}
              onValidate={onValidate}
              contractRates={contractRates.data}
              domainData={domainData}
              fireNumberOptions={fireNumberOptions}
              crewTypeOptions={crewTypeOptions}
              costingActivityOptions={costingActivityOptions}
              rateTypeOptions={rateTypeOptions}
              rateUnitOptions={rateUnitOptions}
              internalOrderOptions={internalOrderOptions}
              costCenterOptions={costCenterOptions}
              fundOptions={fundOptions}
              accountOptions={accountOptions}
            />
          )}
        </>
      )}
      <GoAModal
        heading={`${flyingHours.filter((x) => !x.deleted && x.isChecked).length} line of flying hours will be deleted.`}
        open={showDeleteModal}
        actions={
          <GoAButtonGroup alignment='end'>
            <GoAButton onClick={() => setShowDeleteModal(false)}>No, cancel</GoAButton>
            <GoAButton onClick={() => onFlyingHoursDelete()}>Yes, delete</GoAButton>
          </GoAButtonGroup>
        }
      >
        <p>This action cannot be undone. Are you sure?</p>
      </GoAModal>
    </>
  );
};

export default FlyingHoursTab;
